/**
 * Leagues step: pick favourite leagues (optional). Reuses the app's real league catalog
 * (ids match User.favoriteLeagueIds). Full-width cards show the league's country flag on the
 * left and its name on the right; a search box filters the list.
 * See specs/onboarding-questionnaire/spec.md (Story 3).
 */

import { useMemo, useState } from 'react';
import { View, Text, Pressable, ScrollView, ActivityIndicator, TextInput } from 'react-native';
import { AnimatedMonkey } from '../../AnimatedMonkey/AnimatedMonkey';
import { GameFlag } from '../../../games/GameFlag/GameFlag';
import { countryCodeFromName } from '../../../../src/quiz/helpers/countryFlag.helper';
import { onboardingStyles as s, C } from '../OnboardingFlow.styles';
import { ONBOARDING_TEST_IDS, ONBOARDING_COPY } from '../OnboardingFlow.constants';
import { BackButton } from './BackButton';

export interface OnboardingLeague {
  id: string;
  name: string;
  initials: string;
  /** Country name (e.g. "England", "Spain") — used to render the flag; may be absent/unknown. */
  country?: string;
}

interface LeaguesStepProps {
  leagues: OnboardingLeague[];
  loading: boolean;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onContinue: () => void;
  onBack: () => void;
  progress: number;
}

export function LeaguesStep({
  leagues, loading, selectedIds, onToggle, onContinue, onBack, progress,
}: LeaguesStepProps) {
  const [query, setQuery] = useState('');
  const count = selectedIds.size;
  const hasSelection = count > 0;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leagues;
    return leagues.filter((lg) => lg.name.toLowerCase().includes(q));
  }, [leagues, query]);

  return (
    <>
      <View style={s.topRow}>
        <BackButton onPress={onBack} />
        <View style={s.progressTrack}>
          <View style={[s.progressFill, { width: `${Math.round(progress * 100)}%` }]} />
        </View>
      </View>

      <View style={s.questionHeader}>
        <AnimatedMonkey size={64} />
        <View style={s.smallBubble}>
          <Text style={s.smallBubbleText}>{ONBOARDING_COPY.leaguesPrompt}</Text>
        </View>
      </View>

      <View style={s.leaguesHeader}>
        <Text style={s.heading}>{ONBOARDING_COPY.leaguesHeading}</Text>
        <Text style={s.subheading}>{ONBOARDING_COPY.leaguesSub}</Text>
        <TextInput
          value={query}
          onChangeText={setQuery}
          placeholder={ONBOARDING_COPY.leaguesSearchPlaceholder}
          placeholderTextColor={C.muted}
          autoCapitalize="none"
          autoCorrect={false}
          maxLength={40}
          testID={ONBOARDING_TEST_IDS.LEAGUES_SEARCH}
          style={s.searchInput}
          returnKeyType="search"
        />
      </View>

      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingHorizontal: 24, paddingTop: 12, paddingBottom: 12 }} keyboardShouldPersistTaps="handled">
        {loading ? (
          <ActivityIndicator color={C.accent} style={{ marginTop: 24 }} />
        ) : leagues.length === 0 ? (
          <Text style={s.hintText}>{ONBOARDING_COPY.leaguesEmpty}</Text>
        ) : filtered.length === 0 ? (
          <Text style={s.hintText}>{ONBOARDING_COPY.leaguesNoMatches}</Text>
        ) : (
          <View style={s.leagueGrid}>
            {filtered.map((lg) => {
              const selected = selectedIds.has(lg.id);
              const hasFlag = lg.country != null && countryCodeFromName(lg.country) != null;
              return (
                <Pressable
                  key={lg.id}
                  onPress={() => onToggle(lg.id)}
                  testID={`${ONBOARDING_TEST_IDS.LEAGUE_CHIP}-${lg.id}`}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  accessibilityLabel={lg.name}
                  style={({ pressed }) => [
                    s.leagueTile,
                    {
                      backgroundColor: selected ? 'rgba(167,139,250,0.14)' : C.bubble,
                      borderColor: selected ? C.accent : C.border,
                    },
                    pressed && s.pressed,
                  ]}
                >
                  <View style={s.leagueFlagSlot}>
                    {hasFlag ? (
                      <GameFlag country={lg.country} width={44} height={32} />
                    ) : (
                      <Text style={s.leagueFlagInitials}>{lg.initials}</Text>
                    )}
                  </View>
                  <Text style={s.leagueTileName} numberOfLines={2}>{lg.name}</Text>
                  {selected ? <Text style={s.leagueTileCheck}>✓</Text> : null}
                </Pressable>
              );
            })}
          </View>
        )}
      </ScrollView>

      <View style={s.footer}>
        <Pressable
          onPress={onContinue}
          testID={ONBOARDING_TEST_IDS.LEAGUES_CTA}
          accessibilityRole="button"
          accessibilityLabel={hasSelection ? ONBOARDING_COPY.leaguesContinue(count) : ONBOARDING_COPY.leaguesSkip}
          style={({ pressed }) => [hasSelection ? s.primaryButton : s.ghostButton, pressed && s.pressed]}
        >
          <Text style={hasSelection ? s.primaryButtonText : s.ghostButtonText}>
            {hasSelection ? ONBOARDING_COPY.leaguesContinue(count) : ONBOARDING_COPY.leaguesSkip}
          </Text>
        </Pressable>
      </View>
    </>
  );
}
